import { Button } from '@creditornot/cb-button';
import React, { useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { FormattedMessage } from 'react-intl';

import { CampaignType } from 'apis/restaurant-api/merchant-admin/campaigns/types';
import WoltModal from 'common/components/WoltModal';
import validateYupSchema from 'common/utils/validateYupSchema';

import { CampaignSchedulingFormValues } from '../../../types';
import { CampaignsTargetContent } from '../../../utils/telemetry/constants';
import {
  telemetryNewCampaignInteractionClickCampaignRules,
  telemetryUpdateCampaignInteractionClickCampaignUpdate,
} from '../../../utils/telemetry/telemetry';
import UnsavedChangesModal from '../../modals/UnsavedChangesModal/UnsavedChangesModal';
import { SchedulingDetails } from '../SchedulingDetails';
import SchedulingFormModal from '../SchedulingFormModal';
import { getSchedulesValidationSchema } from '../schedulesValidationSchema';
import { Schedule, SchedulingFormValues } from '../types';
import { parseSchedulesToWeeklySchedule, parseWeeklyScheduleToSchedules } from '../utils';

export type CampaignSchedulingFormItemProps = {
  timezone: string;
  country: Country;
  startDate: CampaignSchedulingFormValues['startDate'];
  endDate: CampaignSchedulingFormValues['endDate'];
  weeklySchedule: CampaignSchedulingFormValues['weeklySchedule'];
  isExistingRunningCampaign: boolean;
  isNewCampaignRoute?: boolean;
  campaignType: CampaignType;
  isEndOfDayCampaign?: boolean;
  'data-testid'?: string;
  onUpdate(formData: {
    startDate: CampaignSchedulingFormValues['startDate'];
    endDate: CampaignSchedulingFormValues['endDate'];
    weeklySchedule: CampaignSchedulingFormValues['weeklySchedule'];
  }): void;
};

export const CampaignSchedulingFormItem = ({
  timezone,
  country,
  startDate,
  endDate,
  weeklySchedule,
  campaignType,
  isExistingRunningCampaign,
  isNewCampaignRoute = false,
  onUpdate,
  isEndOfDayCampaign,
  ...props
}: CampaignSchedulingFormItemProps) => {
  const [isUnsavedChangesModalOpen, setIsUnsavedChangesModalOpen] = useState<boolean>(false);
  const [isSchedulingModalOpen, setIsSchedulingModalOpen] = useState<boolean>(false);

  const handleCloseModals = (isFormDirty?: boolean) => {
    if (isFormDirty) {
      setIsUnsavedChangesModalOpen(true);
    } else {
      setIsUnsavedChangesModalOpen(false);
      setIsSchedulingModalOpen(false);
    }
  };

  const handleSubmitForm = (formData: SchedulingFormValues) => {
    onUpdate({
      startDate: formData.startDate,
      endDate: formData.endDate,
      weeklySchedule:
        formData.timeframe === 'open-hours' ? null : parseSchedulesToWeeklySchedule(formData.schedules || []),
    });

    handleCloseModals();
  };

  const handleChangeSchedulingClick = () => {
    if (isNewCampaignRoute) {
      telemetryNewCampaignInteractionClickCampaignRules(CampaignsTargetContent.NEW.CHANGE_SCHEDULING, campaignType);
    } else {
      telemetryUpdateCampaignInteractionClickCampaignUpdate(
        CampaignsTargetContent.EDIT.CHANGE_SCHEDULING,
        campaignType,
      );
    }
    setIsSchedulingModalOpen(true);
  };

  const defaultSchedules = useMemo<Schedule[] | undefined>(() => {
    if (!weeklySchedule) {
      return;
    }

    return parseWeeklyScheduleToSchedules(weeklySchedule);
  }, [weeklySchedule]);

  return (
    <>
      <Form<SchedulingFormValues>
        initialValues={{
          startDate,
          endDate,
          schedules: defaultSchedules,
          timeframe: defaultSchedules?.length ? ('specific-times' as const) : ('open-hours' as const),
        }}
        onSubmit={handleSubmitForm}
        validate={validateYupSchema(getSchedulesValidationSchema(country))}
      >
        {({ handleSubmit, values, errors, form, dirty, initialValues }) => {
          const isFormDirty = dirty || JSON.stringify(initialValues.schedules) !== JSON.stringify(values.schedules);

          return (
            <WoltModal isOpen={isSchedulingModalOpen} onRequestClose={() => handleCloseModals(isFormDirty)}>
              <SchedulingFormModal
                timeframe={values.timeframe}
                timezone={timezone}
                errors={errors}
                schedules={values.schedules}
                onSubmit={handleSubmit}
                onCloseClick={() => handleCloseModals(isFormDirty)}
                onScheduleChange={schedules => form.change('schedules', schedules)}
                onTimeframeChange={timeframe => {
                  if (timeframe === 'open-hours') {
                    form.change('schedules', undefined);
                  }
                  form.change('timeframe', timeframe);
                }}
                isExistingRunningCampaign={isExistingRunningCampaign}
              />
              <UnsavedChangesModal
                isOpen={isFormDirty && isUnsavedChangesModalOpen}
                onDropChanges={() => {
                  handleCloseModals();
                  form.reset();
                }}
                onClose={() => setIsUnsavedChangesModalOpen(false)}
              />
            </WoltModal>
          );
        }}
      </Form>

      <SchedulingDetails
        data-testid={props['data-testid']}
        startDate={startDate}
        endDate={endDate}
        timezone={timezone}
        timeframe={weeklySchedule ? 'specific-times' : 'open-hours'}
        country={country}
        isEndOfDayCampaign={isEndOfDayCampaign}
        campaignType={campaignType}
        checkVenueCampaignOverlap={isNewCampaignRoute && !isEndOfDayCampaign}
        action={
          !isEndOfDayCampaign && (
            <Button variant="lightBlue" size="small" onClick={handleChangeSchedulingClick}>
              <FormattedMessage id="campaigns.form.scheduling.change-scheduling" />
            </Button>
          )
        }
      />
    </>
  );
};
